import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  userApi,
  OtherUserProfile as OtherUserProfileType,
  ReviewCategory,
} from '../api/user'
import { pageCache } from '../utils/pageCache'
import { toastUtil } from '../utils/toast'
import { invalidateReviewCaches } from '../utils/homeFeedCache'
import { goBackOrHome } from '../utils/navigation'
import { FollowStatus } from '../constants/follow'
import { ReviewListType } from '../constants/reviewListType'
import { useAuthStore } from '../stores/authStore'
import Gap from '../components/Gap'
import LoadingSpinner from '../components/LoadingSpinner'
import UserStatisticInfo from '../components/UserStatisticInfo'
import OtherUserProfileInfo from '../components/OtherUserProfileInfo'
import DefaultFolderGrid from '../components/DefaultFolderGrid'
import MediumBadgeButton from '../components/MediumBadgeButton'
import PlateList from '../components/PlateList'
import AppBar from '../components/AppBar'
import ConfirmDialog from '../components/ConfirmDialog'
import TextListBottomSheet from '../components/TextListBottomSheet'
import ReportModal from '../components/ReportModal'

const OtherUserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { userId: currentUserId } = useAuthStore()
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `user-profile:${userId ?? ''}`
  const cached = pageCache.get<OtherUserProfileType>(cacheKey)
  const [user, setUser] = useState<OtherUserProfileType | null>(cached)
  const [categories, setCategories] = useState<ReviewCategory[]>(
    cached?.categories ?? []
  )
  const [isLoading, setIsLoading] = useState(!cached)
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false)
  const [isReportOpen, setIsReportOpen] = useState(false)
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false)
  const [isBlocking, setIsBlocking] = useState(false)

  useEffect(() => {
    if (!userId) {
      toastUtil.error('사용자 정보를 불러오는데 실패했습니다')
      navigate('/main-home')
      return
    }

    const loadData = async () => {
      // 캐시로 이미 그려져 있으면 로딩 화면으로 덮지 않는다
      if (!pageCache.has(cacheKey)) {
        setIsLoading(true)
      }
      try {
        // 프로필 정보와 카테고리를 한 번에 호출
        const profileResponse = await userApi.getOtherUserProfile(userId)

        pageCache.set(cacheKey, profileResponse.user)
        setUser(profileResponse.user)
        setCategories(profileResponse.user.categories || [])
      } catch (error) {
        /*
         * 여기서 토스트를 띄우지 않는다.
         *
         * apiClient 인터셉터가 이미 서버 메시지로 하나 띄운다. 여기서 또
         * 띄우면 두 개가 겹쳐 뜬다 — 차단했거나 탈퇴한 사용자를 누르면
         * "사용자를 찾을 수 없습니다"와 "프로필을 불러오는데 실패했습니다"가
         * 같이 떴다.
         *
         * 나가는 것만 여기서 한다.
         */
        console.error('데이터 로드 실패:', error)
        navigate('/main-home')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [userId, navigate])

  const handleFollowClick = async () => {
    if (!user || !userId) return

    // 서버 응답을 기다리지 않고 버튼을 먼저 바꾼다.
    // 팔로우 API는 알림 생성·FCM 전송까지 하느라 수백 ms가 걸리는데, 이 화면은
    // 그 뒤 프로필 재조회까지 기다려서 왕복 두 번 동안 버튼이 멈춰 보였다.
    const previousStatus = user.follow_status
    const optimisticStatus =
      previousStatus === FollowStatus.NONE
        ? user.is_private
          ? FollowStatus.REQUESTED
          : FollowStatus.FOLLOWING
        : FollowStatus.NONE

    const applyStatus = (next: FollowStatus) => {
      setUser((prev) => (prev ? { ...prev, follow_status: next } : prev))
    }

    applyStatus(optimisticStatus)

    try {
      if (previousStatus === FollowStatus.NONE) {
        const response = await userApi.requestFollow(user.id)
        // 공개/비공개 판정은 서버가 하므로 응답으로 실제 상태를 맞춘다
        applyStatus(
          response.status === 'accepted'
            ? FollowStatus.FOLLOWING
            : FollowStatus.REQUESTED
        )
        toastUtil.success(response.message)
      } else if (previousStatus === FollowStatus.REQUESTED) {
        await userApi.cancelFollow(user.id)
        toastUtil.success('팔로우 요청이 취소되었습니다')
      } else if (previousStatus === FollowStatus.FOLLOWING) {
        await userApi.cancelFollow(user.id)
        toastUtil.success('팔로잉이 해제되었습니다')
      }
    } catch (error) {
      applyStatus(previousStatus)
      console.error('팔로우 처리 실패:', error)
      // apiClient 인터셉터에서 이미 에러 메시지를 표시하므로 여기서는 추가 토스트를 표시하지 않음
      return
    }

    // 팔로워 수·카테고리 등 나머지 정보 갱신. 버튼 상태는 이미 반영됐으므로
    // 여기서 실패해도 되돌리지 않는다.
    try {
      const profileResponse = await userApi.getOtherUserProfile(userId)
      setUser(profileResponse.user)
      setCategories(profileResponse.user.categories || [])
    } catch (error) {
      console.error('프로필 갱신 실패:', error)
    }
  }

  /**
   * 차단한다.
   *
   * 차단하면 이 프로필은 서버에서 404가 되므로 화면에 머물 수 없다. 머물면
   * 다음 갱신에서 "불러오는데 실패했습니다"가 뜬다. 그래서 바로 빠져나간다.
   *
   * 캐시도 비운다 — pageCache 에 남아 있으면 뒤로 돌아올 때 차단한 사람의
   * 프로필이 다시 그려진다.
   */
  const handleBlock = async () => {
    if (!userId || isBlocking) return

    setIsBlocking(true)
    try {
      await userApi.blockUser(userId)
      pageCache.remove(cacheKey)
      /*
       * 피드가 들고 있던 목록도 버린다.
       *
       * 서버는 다음 조회부터 이 사람 기록을 안 내려주지만, 홈은 떠나기 전에
       * 찍어 둔 스냅샷으로 되살아난다. 그래서 차단하고 홈으로 돌아가면
       * 방금 차단한 사람의 기록이 그대로 있었다 — 새로고침해야 사라졌다.
       */
      invalidateReviewCaches()
      setIsBlockDialogOpen(false)
      toastUtil.success('차단되었습니다')
      goBackOrHome(navigate)
    } catch (error) {
      console.error('차단 실패:', error)
      // 메시지는 apiClient 인터셉터가 띄운다
    } finally {
      setIsBlocking(false)
    }
  }

  const handleCategoryClick = (categoryId: string, name?: string) => {
    if (!userId) return
    // 이름을 state로 함께 넘겨 로딩 중에도 올바른 제목이 보이게 한다(빈 제목 깜빡임 방지)
    navigate(`/user/${userId}/plate-category/${categoryId}`, {
      state: { categoryName: name },
    })
  }

  const handleCategoryAdded = async () => {
    if (!userId) return
    try {
      const response = await userApi.getReviewCategories(userId)
      setCategories(response.categories)
    } catch (error) {
      console.error('카테고리 목록 로드 실패:', error)
    }
  }

  const handleBack = () => {
    goBackOrHome(navigate)
  }

  const isMine = currentUserId === userId

  const displayPrivateBox = () => {
    return (
      <div className="w-full flex flex-col items-center px-16px pt-[15vh] pb-40px">
        <img
          src="/images/lock-icon.png"
          alt="비공개"
          className="w-96px h-96px object-contain"
        />

        <Gap height={16} />

        <div className="font-pretendard font-regular text-16px leading-180% text-textColorText3 text-center">
          비공개 계정입니다.
          <br />
          팔로우 승인 후 열람 가능합니다.
        </div>
      </div>
    )
  }

  const displayCategoryGrid = () => {
    if (!userId) return null

    return (
      <>
        <div className="app-container w-full px-20px">
          {/* 기본 폴더 */}
          <DefaultFolderGrid userId={userId} />
        </div>

        <div className="app-container w-full px-20px">
          {/* divider */}
          <div className="w-full h-1px bg-brandPrimary-10% shrink-0"></div>
        </div>

        <Gap height={16} />

        <div className="app-container w-full">
          {/* 플레이트리스트 */}
          <PlateList
            categories={categories}
            onCategoryClick={handleCategoryClick}
            isMine={isMine}
            onCategoryAdded={handleCategoryAdded}
          />
        </div>

        <Gap height={16} />
      </>
    )
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingSpinner size={28} borderWidth={2} />
      </div>
    )
  }

  if (!user) {
    return null
  }

  const canViewPrivateProfileContent =
    isMine || !user.is_private || user.follow_status === FollowStatus.FOLLOWING

  const renderFollowAndSimilarity = () => {
    const getFollowLabel = () => {
      if (user.follow_status === FollowStatus.NONE) {
        return '팔로우'
      } else if (user.follow_status === FollowStatus.REQUESTED) {
        return '요청중'
      } else {
        return '팔로잉'
      }
    }

    const getSimilarityLabel = () => {
      return `나와의 유사도 ${user.similarity ?? 0}%`
    }

    return (
      <div className="w-full flex items-center justify-center gap-10px">
        <MediumBadgeButton
          label={getFollowLabel()}
          onClick={handleFollowClick}
          variant={
            user.follow_status === FollowStatus.NONE ? 'default' : 'inverse'
          }
          className="flex-1"
        />
        {/*
          누르면 이 %가 왜 나왔는지 — 7축 분포와 근거 세 문장.

          비공개 계정은 %만 보여주고 분석으로는 들여보내지 않는다. 서버가
          403으로 막으므로(UserController@getTasteProfile) 눌리게 두면
          "비공개 계정입니다" 한 줄짜리 빈 화면에 들어갔다 되돌아 나와야 한다.
          리뷰 수·팔로워·팔로잉과 같은 방식으로 핸들러만 뗀다.
        */}
        <MediumBadgeButton
          label={getSimilarityLabel()}
          variant="inverse"
          className="flex-1"
          onClick={
            canViewPrivateProfileContent
              ? () => navigate(`/user/${userId}/taste-profile`)
              : undefined
          }
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      {/* AppBar */}
      <AppBar
        title={user.username}
        titleAlign="left"
        onBack={handleBack}
        titleFontSize={20}
        rightElement={
          // 내 프로필에서는 띄우지 않는다. 이미 차단한 사람은 이 화면 자체가
          // 열리지 않으므로(서버가 404) 시트에 '차단 해제'는 없다 —
          // 해제는 설정 > 차단한 사용자에서 한다.
          userId && userId !== currentUserId ? (
            <button
              type="button"
              onClick={() => setIsActionSheetOpen(true)}
              aria-label="더보기"
              /*
                오른쪽 여백 20px.

                왼쪽 뒤로가기는 아이콘 PNG 안에 투명 여백이 들어 있어
                화면 끝에서 약 19px 들어와 보인다(192px 중 76px이 비어 있고
                48px로 줄여 그린다). 이쪽은 그런 여백이 없어 그냥 두면 점이
                화면 끝에 붙는다. 앱 표준 여백에 맞춰 직접 띄운다.

                viewBox를 점에 꼭 맞춰 뒀으므로 pr 값이 곧 화면 끝까지의 거리다.
              */
              // 색까지 자세히보기·플레이트리스트와 같은 값으로. 빠져 있어서
              // 이 화면 점만 주변 글자색을 물려받아 다르게 보였다.
              className="h-48px flex items-center justify-end pr-20px hover:opacity-70 text-[#6B5E46]"
            >
              {/* 가로로 놓인 점 세 개 */}
              <svg width="18" height="4" viewBox="0 0 18 4" fill="none">
                <circle cx="1.8" cy="2" r="1.8" fill="currentColor" />
                <circle cx="9" cy="2" r="1.8" fill="currentColor" />
                <circle cx="16.2" cy="2" r="1.8" fill="currentColor" />
              </svg>
            </button>
          ) : undefined
        }
      />

      <TextListBottomSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        items={[
          { text: '신고', onClick: () => setIsReportOpen(true) },
          { text: '차단', onClick: () => setIsBlockDialogOpen(true) },
        ]}
      />

      {/*
        프로필에서 누르는 신고는 '이 사람'이 대상이다. 특정 기록이 아니라
        사람이 문제라서 왔기 때문에, 기록 하나를 골라 붙이지 않는다.
      */}
      <ReportModal
        isOpen={isReportOpen}
        reportUserId={userId}
        onClose={() => setIsReportOpen(false)}
      />

      <ConfirmDialog
        isOpen={isBlockDialogOpen}
        title={`${user.nickname}님을 차단하시겠어요?`}
        message="차단 사실은 알리지 않습니다"
        confirmText={isBlocking ? '차단 중...' : '차단하기'}
        cancelText="취소"
        onCancel={() => setIsBlockDialogOpen(false)}
        onConfirm={handleBlock}
      />

      <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
        <div className="app-container w-full px-20px">
          <Gap height={16} />

          {/* 사용자 정보 */}
          <OtherUserProfileInfo
            nickname={user.nickname}
            profileImage={user.profile_image || undefined}
            username={user.username}
            bio={user.bio}
            mealmateCount={user.mealmate_count ?? 0}
            tasteTrait={user.taste_trait}
            isMutualFollow={user.is_mutual_follow ?? false}
            emphasizeNicknameInProfileRow
            isOtherUserProfile={true}
          />

          <Gap height={16} />

          {/* 통계 정보 */}
          <UserStatisticInfo
            reviewCount={user.review_count}
            followerCount={user.follower_count}
            followingCount={user.following_count}
            onReviewClick={
              canViewPrivateProfileContent
                ? () =>
                    navigate(
                      `/reviews?type=${ReviewListType.REVIEWS}&userId=${userId}`
                    )
                : undefined
            }
            onFollowerClick={
              canViewPrivateProfileContent
                ? () => navigate(`/follow-list/followers?userId=${userId}`)
                : undefined
            }
            onFollowingClick={
              canViewPrivateProfileContent
                ? () => navigate(`/follow-list/following?userId=${userId}`)
                : undefined
            }
          />

          <Gap height={16} />

          {/* 팔로우, 유사도 */}
          {renderFollowAndSimilarity()}
        </div>

        <Gap height={16} />

        {/*
          카테고리.

          **여기에 스크롤을 또 달면 안 된다.** 바깥(위 div)이 이미
          `overflow-y-auto` 인데 그 안에서 `flex-1` 을 주면, 이 칸이 남은
          높이를 정확히 채워 버려서 바깥은 넘칠 것이 없어진다 — 프로필·통계는
          붙박이가 되고 이 칸만 안에서 구른다. 세로 화면에서는 눈에 잘 안
          띄지만 가로로 돌리면 프로필이 화면을 거의 다 먹고 손바닥만 한 틈만
          움직인다(2026-10-17 실측: 바깥 scrollHeight == clientHeight).
          내 프로필(MainMy)은 스크롤이 하나뿐이라 멀쩡했다. 그쪽에 맞춘다.
        */}
        <div className="w-full">
          {canViewPrivateProfileContent ? (
            <>{displayCategoryGrid()}</>
          ) : (
            <>{displayPrivateBox()}</>
          )}
        </div>
      </div>
    </div>
  )
}

export default OtherUserProfile
